import { css } from '@emotion/react'
import { Component } from 'react'
import { observer, inject } from 'mobx-react'
import styled from '@emotion/styled'
import { withRouterProps } from '../../utils'
import { Button, Drawer, Loading } from '../../components'
import { PageContainer, PageTitle } from './ProfileStyle'

import AboutYouCard from './about-you/AboutYouCard'
import AboutYouEdit from './about-you/AboutYouEdit'
import SimpleAboutYouEdit from './about-you/SimpleAboutYouEdit'

import AboutFamilyCard from './about-your-family/AboutFamilyCard'
import AboutFamilyEdit from './about-your-family/AboutFamilyEdit'

import ContactInfoCard from './contact-info/ContactInfoCard'
import ContactInfoEdit from './contact-info/ContactInfoEdit'
import SimpleContactInfoEdit from './contact-info/SimpleContactInfoEdit'

import HistoricalDataCard from './historical-data/HistoricalDataCard'
import HistoricalDataDrawer from './historical-data/HistoricalDataDrawer'

import LoginCard from './login/LoginCard'
import LegalNoticesCard from './legal-notices/LegalNoticesCard'

import ServicesCard from './services/ServicesCard'
import AutoRiskAdjustEdit from './services/AutoRiskAdjustEdit'
import DiscontinueForm from './services/DiscontinueForm'
import DiscontinuePTDPlusForm from './services/DiscontinuePTDPlusForm'
import DiscontinueRetail from './services/DiscontinueRetail'
import ReinstateRetail from './services/ReinstateRetail'

const mapDrawerToTitle = {
  aboutYou: 'About you',
  aboutFamily: 'About your family',
  contactInfo: 'Contact & communications',
  historicalData: 'Historical data',
  autoRiskLevelAdjust: 'GuidedChoice services',
  discontinue: 'GuidedChoice services',
  discontinuePTDPlus: 'GuidedChoice services',
  discontinueRetail: 'Discontinue subscription',
  reinstateRetail: 'Reinstate automatic renewal',
}

class Profile extends Component {
  state = { drawer: '', drawerWidth: '0%', status: 'loading' }

  renderChildren = type => {
    const { features } = this.props.store

    const drawerContentMap = {
      aboutYou: features.enablePreferredName ? (
        <AboutYouEdit onClose={this.handleClose} />
      ) : (
        <SimpleAboutYouEdit onClose={this.handleClose} />
      ),
      aboutFamily: <AboutFamilyEdit onClose={this.handleClose} />,
      contactInfo: features.enableMobilePhone ? (
        <ContactInfoEdit onClose={this.handleClose} />
      ) : (
        <SimpleContactInfoEdit onClose={this.handleClose} />
      ),
      historicalData: <HistoricalDataDrawer onClose={this.handleClose} />,
      autoRiskLevelAdjust: <AutoRiskAdjustEdit onClose={this.handleClose} />,
      discontinue: <DiscontinueForm onClose={this.handleClose} />,
      discontinuePTDPlus: <DiscontinuePTDPlusForm onClose={this.handleClose} />,
      discontinueRetail: (
        <DiscontinueRetail
          onClose={() => {
            this.props.store.getSubscriptions()
            this.handleClose()
          }}
        />
      ),
      reinstateRetail: <ReinstateRetail onClose={this.handleClose} />,
    }

    return drawerContentMap[type] || null
  }

  showAboutYou = () => this.setState({ drawer: 'aboutYou', drawerWidth: '80%' })

  showAboutFamily = () => this.setState({ drawer: 'aboutFamily', drawerWidth: '80%' })

  showContactInfo = () => this.setState({ drawer: 'contactInfo', drawerWidth: '80%' })

  showHistoricalData = () => this.setState({ drawer: 'historicalData', drawerWidth: '80%' })

  showAutoRiskLevelAdjust = () =>
    this.setState({ drawer: 'autoRiskLevelAdjust', drawerWidth: '80%' })

  showDiscontinue = () => this.setState({ drawer: 'discontinue', drawerWidth: '50%' })

  showDiscontinuePTDPlus = () => this.setState({ drawer: 'discontinuePTDPlus', drawerWidth: '80%' })

  showDiscontinueRetail = () => this.setState({ drawer: 'discontinueRetail', drawerWidth: '50%' })

  showReinstateRetail = () => this.setState({ drawer: 'reinstateRetail', drawerWidth: '50%' })

  handleClose = () => this.setState({ drawer: '', drawerWidth: '0%' })

  handleUnretire = async () => {
    await this.props.store.config.disableSpendingMode()
    window.location.reload()
  }

  fetchRetirementStatusesForSpendDown = async () => {
    try {
      await this.props.store.getPrimaryRetirementStatus()
      if (this.props.store.person.includeSpouse) {
        await this.props.store.getSpouseRetirementStatus()
      }
      this.setState({
        ...this.state,
        status: 'done',
      })
    } catch (err) {
      console.error(err)
      this.setState({ status: 'error' })
    }
  }

  componentDidMount() {
    // Retirement status endpoints are unavailable without plan access.
    if (this.props.store.hasPlanAccess) {
      this.fetchRetirementStatusesForSpendDown()
    } else {
      this.setState({ status: 'done' })
    }
    this.props.store.getSubscriptions()
  }

  render() {
    const { features, config, hasPlanAccess } = this.props.store
    const { changeSavingsList } = this.props.store.userLogs

    const { isRetail } = config

    if (this.state.status === 'loading') {
      return <Page.Loading />
    }

    if (this.state.status === 'error') {
      return (
        <Page.ErrorMessage>Oops! Something went wrong, please try again later</Page.ErrorMessage>
      )
    }

    return (
      <PageContainer data-no-mobile-gutter>
        <PageTitle>Profile</PageTitle>

        <AboutYouCard showAboutYou={this.showAboutYou} />

        {hasPlanAccess && (features.enableSpouse || features.enableDependents) && (
          <AboutFamilyCard showAboutFamily={this.showAboutFamily} />
        )}

        <ContactInfoCard showContactInfo={this.showContactInfo} />

        <ServicesCard
          showAutoRiskLevelAdjust={this.showAutoRiskLevelAdjust}
          showDiscontinue={this.showDiscontinue}
          showDiscontinuePTDPlus={this.showDiscontinuePTDPlus}
          showDiscontinueRetail={this.showDiscontinueRetail}
          showReinstateRetail={this.showReinstateRetail}
          isRetail={isRetail}
        />

        {hasPlanAccess &&
          (features.enableLoginHistory ||
            features.enablePreviousGuides ||
            changeSavingsList.length > 0) && (
            <HistoricalDataCard showHistoricalData={this.showHistoricalData} />
          )}

        {features.editLoginInfo && <LoginCard />}

        <LegalNoticesCard />

        {config.template.overallPageComponent === 'Simplified' && (
          <div
            css={css`
              text-align: right;
              margin-bottom: 45px;
              max-width: 900px;
            `}>
            <Button variant='primary' width='100px' onClick={() => this.props.navigate('/overall')}>
              Done
            </Button>
          </div>
        )}

        <Drawer
          active={Boolean(this.state.drawer)}
          title={mapDrawerToTitle[this.state.drawer]}
          subtitle='Edit profile'
          width={this.state.drawerWidth}>
          {this.state.drawer && this.renderChildren(this.state.drawer)}
        </Drawer>

        {/* Hidden shortcut out of spend-down mode, ported as-is from the retired Help
            tab: same bottom-right corner, same always-rendered behavior. */}
        <div
          css={css`
            width: 30px;
            height: 30px;
            opacity: 0;
            position: fixed;
            bottom: 0;
            right: 0;
            cursor: default;
          `}
          onClick={this.handleUnretire}>
          Unretire
        </div>
      </PageContainer>
    )
  }
}

export default withRouterProps(inject('store')(observer(Profile)))

const Page = styled.div``

Page.Loading = () => (
  <div
    css={css`
      margin: 20px;
    `}>
    <Loading />
  </div>
)

Page.ErrorMessage = styled.div`
  color: crimson;
  text-align: center;
  margin: 20px 0;
`
